import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Await, Link, useFetchers } from "react-router";

import { ChevronDownIcon, ChevronUpIcon } from "@radix-ui/react-icons";

import { SkeletonAwait } from "~/components/suspense/skeleton";
import Image from "~/components/ui/image";
import { useURL } from "~/contexts";
import {
  isFailedEditSettle,
  nextLastEdit,
  resolveEditBaseline,
} from "~/hec/basket/basket-submission.logic";
import type { LastQuantityEdit } from "~/hec/basket/types";
import { QuantityStepper } from "~/hec/components/ui/quantity-stepper";
import { isScheduled } from "~/hec/types/product";
import { isOutOfStock } from "~/hec/utils/inventory-state";
import { rewriteSfccContentLinks } from "~/hec/utils/sfcc-content-links";
import type { C_ProductInfo, VariationGroup } from "~/lib/app.types";
import { useOnCompleted } from "~/lib/remix/fetcher";
import { cn } from "~/lib/ui";
import { toProductUrl, toVariantURL } from "~/lib/utils/url";
import {
  ProductImageLinkPrimitive,
  ProductNameLinkPrimitive,
  ProductPricePrimitive,
  ProductVariantsPrimitive,
  RemovePrimitive,
} from "~/shop/components/product-row.primitives";
import { getVariantImage } from "~/shop/utils/image";
import { getLineItemPrices } from "~/shop/utils/line-item-price";
import { resolvePromotionCallout } from "~/shop/utils/promotion-callout";
import { decodeScapiPriceInfo } from "~/shop/utils/scapi-price";

import { useBasket, useBasketActions } from "../hooks/basket-hooks";
import { useBasketLineItem } from "./basket-line-item.hooks";
import { mapVariationValuesToRows } from "./basket-line-item.variations";
import { useBasketRows } from "./basket-rows-context";

// Helper function to find the appropriate variation group or create one from variants
const findProductVariationGroup = (
  productDetails: C_ProductInfo | undefined,
) => {
  if (!productDetails) return undefined;

  let productVariationGroup = productDetails.variationGroups?.find(group => {
    return Object.entries(group.variationValues).some(
      ([key, value]) => productDetails.variationValues?.[key] === value,
    );
  });

  if (!productVariationGroup && productDetails.variants) {
    const matchingVariant = productDetails.variants.find(variant => {
      return Object.entries(variant.variationValues || {}).every(
        ([key, value]) => productDetails.variationValues?.[key] === value,
      );
    });

    if (matchingVariant) {
      productVariationGroup = {
        orderable: matchingVariant.orderable ?? false,
        price: matchingVariant.price ?? 0,
        productId: matchingVariant.productId,
        variationValues: matchingVariant.variationValues || {},
      };
    }
  }

  return productVariationGroup;
};

export const BasketLineItemProductImage = ({
  className,
  size = "large",
  productItems,
}: {
  className?: string;
  size?: "xs" | "medium" | "large";
  productItems?: C_ProductInfo[];
}) => {
  const basket = useBasket();
  const products = productItems || basket?.products || [];
  const { lineItem, idScope } = useBasketLineItem();
  const url = useURL();
  const getVariantURL = useCallback(
    (
      variationGroup: VariationGroup | undefined,
      product: { id?: string; name?: string },
    ) => {
      if (!variationGroup) return url(toProductUrl(product));
      return url(toVariantURL(variationGroup, product.name));
    },
    [url],
  );
  if (!products) return null;

  return (
    <SkeletonAwait
      resolve={products}
      className={cn("h-24 w-24 rounded-md", className)}
    >
      {products => {
        const productDetails = products.find(p => p.id === lineItem.productId);
        if (!productDetails) return null;
        const productVariationGroup = findProductVariationGroup(productDetails);
        return (
          <ProductImageLinkPrimitive
            href={getVariantURL(productVariationGroup, productDetails)}
            product={productDetails}
            size={size}
            className={className}
            // imgSm + imgLg both render (responsive), so suffix with the size
            // variant to keep the two unique; QA prefix-matches
            // `<scope>-item-image-${sku}-` (e.g. cart-item- / minicart-item-).
            data-testid={`${idScope}-image-${lineItem.productId}-${size}`}
          />
        );
      }}
    </SkeletonAwait>
  );
};
BasketLineItemProductImage.displayName = "BasketLineItem.ProductImage";

export const BasketLineItemProductName = ({
  className,
  productItems,
}: {
  className?: string;
  productItems?: C_ProductInfo[];
}) => {
  const basket = useBasket();
  const products = productItems || basket?.products || [];
  const { lineItem, idScope } = useBasketLineItem();
  const url = useURL();
  const getVariantURL = useCallback(
    (
      variationGroup: VariationGroup | undefined,
      product: { id?: string; name?: string },
    ) => {
      if (!variationGroup) return url(toProductUrl(product));
      return url(toVariantURL(variationGroup, product.name));
    },
    [url],
  );
  if (!products) return null;

  return (
    <Suspense
      fallback={
        <h3 className={cn(className)}>
          <a
            href="/#"
            onClick={e => e.preventDefault()}
            data-testid={`${idScope}-name-${lineItem.productId}`}
          >
            {lineItem.productName}
          </a>
        </h3>
      }
    >
      <Await resolve={products}>
        {products => {
          const productDetails = products.find(
            p => p.id === lineItem.productId,
          );
          if (!productDetails) {
            // COM-902: a partial basket payload (product read timed out) — or
            // an action response that landed before the re-load — has no
            // product for this line. Name it from the basket line itself and
            // link the product id, rather than render a row with no name.
            if (!lineItem.productId || !lineItem.productName) return null;
            return (
              <ProductNameLinkPrimitive
                href={getVariantURL(undefined, {
                  id: lineItem.productId,
                  name: lineItem.productName,
                })}
                name={lineItem.productName}
                className={className}
                data-testid={`${idScope}-name-${lineItem.productId}`}
              />
            );
          }
          const productVariationGroup =
            findProductVariationGroup(productDetails);
          return (
            <ProductNameLinkPrimitive
              href={getVariantURL(productVariationGroup, productDetails)}
              name={productDetails.name}
              className={className}
              data-testid={`${idScope}-name-${lineItem.productId}`}
            />
          );
        }}
      </Await>
    </Suspense>
  );
};
BasketLineItemProductName.displayName = "BasketLineItem.ProductName";

export const BasketLineItemProductPriceTotal = ({
  className,
}: {
  className?: string;
}) => {
  const { lineItem, idScope } = useBasketLineItem();
  const { salePrice, currentPrice } = getLineItemPrices(lineItem);
  return (
    <ProductPricePrimitive
      currentPrice={currentPrice}
      salePrice={salePrice}
      className={className}
      data-testid={`${idScope}-line-total-${lineItem.productId}`}
    />
  );
};
BasketLineItemProductPriceTotal.displayName =
  "BasketLineItem.ProductPriceTotal";

export const BasketLineItemProductPriceUnit = ({
  className,
  productItems,
}: {
  className?: string;
  productItems?: C_ProductInfo[];
}) => {
  const basket = useBasket();
  const products = productItems || basket?.products || [];
  const { lineItem, idScope } = useBasketLineItem();

  // Fallback = the prior basket-derived unit price. Guarantees this is never
  // worse than before: if the product or its `c_priceInfo` isn't available we
  // render exactly what we did previously.
  const fallback = getLineItemPrices(lineItem);
  const fallbackEl = (
    <ProductPricePrimitive
      currentPrice={fallback.unitCurrentPrice}
      salePrice={fallback.unitSalePrice}
      className={className}
      data-testid={`${idScope}-unit-price-${lineItem.productId}`}
    />
  );

  return (
    <Suspense fallback={fallbackEl}>
      <Await resolve={products} errorElement={fallbackEl}>
        {products => {
          const productDetails = products.find(
            p => p.id === lineItem.productId,
          );
          // SFRA renders the cart unit price through the SAME `pricing/main`
          // include as PLP/PDP — i.e. the product's pricebook list/sales
          // (`c_priceInfo`), NOT the basket line item's `c_standardPrice` (a
          // custom attr populated by a hook that isn't deployed in Workers).
          const decoded = productDetails
            ? decodeScapiPriceInfo(
                (productDetails as { c_priceInfo?: unknown }).c_priceInfo,
              )
            : null;
          if (!decoded) return fallbackEl;
          // decoded.price = list (struck) / decoded.salePrice = sales (shown)
          // when on sale; decoded.price = the single price otherwise.
          return (
            <ProductPricePrimitive
              currentPrice={decoded.price}
              salePrice={decoded.salePrice}
              className={className}
              data-testid={`${idScope}-unit-price-${lineItem.productId}`}
            />
          );
        }}
      </Await>
    </Suspense>
  );
};
BasketLineItemProductPriceUnit.displayName = "BasketLineItem.ProductPriceUnit";

export const BasketLineItemProductVariants = ({
  className,
}: {
  className?: string;
}) => {
  const { lineItem } = useBasketLineItem();
  const { products } = useBasket() ?? {};
  if (!products) return null;
  return (
    <Await resolve={products}>
      {products => {
        const productDetails = products?.find(p => p.id === lineItem.productId);
        const rows = mapVariationValuesToRows(productDetails);
        return (
          <ProductVariantsPrimitive variations={rows} className={className} />
        );
      }}
    </Await>
  );
};
BasketLineItemProductVariants.displayName = "BasketLineItem.ProductVariants";

export const BasketLineItemCrossPromo = ({
  className,
}: {
  className?: string;
}) => {
  const { lineItem, idScope } = useBasketLineItem();
  const { products } = useBasket() ?? {};
  if (!products) return null;
  return (
    <Await resolve={products}>
      {products => {
        const productDetails = products?.find(p => p.id === lineItem.productId);
        if (!productDetails?.c_cartCrossPromoMessagingEnable) return null;
        if (!productDetails.c_crossPromoMessaging) return null;
        if (
          !isScheduled(
            productDetails.c_crossPromoMessagingDisplayFrom,
            productDetails.c_crossPromoMessagingDisplayTo,
          )
        ) {
          return null;
        }
        return (
          <div
            className={cn("text-sm", className)}
            dangerouslySetInnerHTML={{
              __html: rewriteSfccContentLinks(
                productDetails.c_crossPromoMessaging,
              ),
            }}
            data-testid={`${idScope}-cross-promo-${lineItem.productId}`}
          />
        );
      }}
    </Await>
  );
};
BasketLineItemCrossPromo.displayName = "BasketLineItem.CrossPromo";

/**
 * SFCC-11931: applied-promotion callout for item-level promos (e.g. a % off
 * products coupon). SFRA renders `priceAdjustment.promotion.calloutMsg.markup`
 * under the line-item price (`productCardProductPromotions.isml`,
 * `encoding="off"`); the SCAPI equivalent is the ShopperPromotions
 * `calloutMsg`, delivered here via the basket context's `promotionCallouts`
 * map — see `resolvePromotionCallout` (shop/utils/promotion-callout) for why
 * the adjustment's own `itemText` is never used. No callout configured → no
 * message. Merchant-authored BM markup — same trust level as
 * `c_crossPromoMessaging` above.
 */
export const BasketLineItemPromotions = ({
  className,
}: {
  className?: string;
}) => {
  const { lineItem, idScope } = useBasketLineItem();
  const { promotionCallouts } = useBasket() ?? {};
  const callouts = (lineItem.priceAdjustments ?? [])
    .map(adjustment => ({
      key: adjustment.priceAdjustmentId ?? adjustment.promotionId,
      callout: resolvePromotionCallout(adjustment, promotionCallouts),
    }))
    .filter((entry): entry is { key: string; callout: string } =>
      Boolean(entry.key && entry.callout),
    );
  if (callouts.length === 0) return null;
  // SFRA `.cart-page .line-item-promo` parity: 14px, weight 300, per-site
  // brand color (`$cart-page-promo-color` → `--promo-callout-color`).
  return (
    <div
      className={cn(
        "space-y-0.5 font-grotesque text-sm font-light text-promo-callout",
        className,
      )}
      data-testid={`${idScope}-promotions-${lineItem.productId}`}
    >
      {callouts.map(entry => (
        <div
          key={entry.key}
          dangerouslySetInnerHTML={{ __html: entry.callout }}
        />
      ))}
    </div>
  );
};
BasketLineItemPromotions.displayName = "BasketLineItem.Promotions";

/**
 * SFCC-11796: flags a line item whose product went out of stock while sitting in
 * the cart (added when available, later OOS). Availability lives on the product
 * (`inventory`), not the SCAPI line item, so we look the product up in the
 * basket's already-fetched `products` and reuse the canonical `isOutOfStock`
 * (which excludes pre/back-order and never false-flags products without an
 * inventory record). Rendered in the row `messaging` slot on both the cart page
 * and the minicart.
 */
export const BasketLineItemOosBadge = ({
  className,
}: {
  className?: string;
}) => {
  const { lineItem, idScope } = useBasketLineItem();
  const { products } = useBasket() ?? {};
  if (!products) return null;
  return (
    <Await resolve={products}>
      {products => {
        const productDetails = products?.find(p => p.id === lineItem.productId);
        if (!isOutOfStock(productDetails?.inventory)) return null;
        return (
          <p
            className={cn("text-sm font-medium text-erroralpha-11", className)}
            data-testid={`${idScope}-oos-badge-${lineItem.productId}`}
          >
            Out of Stock
          </p>
        );
      }}
    </Await>
  );
};
BasketLineItemOosBadge.displayName = "BasketLineItem.OosBadge";

export const BasketLineItemRemoveProduct = ({
  className,
}: {
  className?: string;
}) => {
  const { lineItem, isStale, setIsStale, idScope, rowIndex } =
    useBasketLineItem();
  const { remove } = useBasketActions();
  const { removeLineItem } = useBasketRows() ?? {};
  const itemId = lineItem?.itemId;

  // Spinner drives off the global fetcher list — catches both the snap-aware
  // path (provider's removeActions fetcher) and the fallback path (this
  // component's local useBasketActions fetcher). Anonymous fetchers per
  // `feedback_basket_fetcher_split.md`.
  const fetchers = useFetchers();
  const isRemoving = fetchers.some(
    f =>
      f.state !== "idle" &&
      f.formData?.get("action") === "remove" &&
      f.formData?.get("itemId") === itemId,
  );

  // COM-63 (P1-1 B/C): a CONTAINED remove failure keeps the row, and a one-way
  // `setIsStale(true)` latched it at opacity-50 forever. Clear only on OUR
  // remove's true→false transition — `setIsStale` is shared across the row's
  // actions (save-for-later dims through it too), so a plain
  // `setIsStale(isRemoving)` mirror would fire `false` on mount and on
  // unrelated re-renders and clear the other action's dim.
  const wasRemovingRef = useRef(false);
  useEffect(() => {
    if (isRemoving) {
      wasRemovingRef.current = true;
      setIsStale(true);
    } else if (wasRemovingRef.current) {
      wasRemovingRef.current = false;
      setIsStale(false);
    }
  }, [isRemoving, setIsStale]);

  if (!itemId) return null;

  return (
    <RemovePrimitive
      onClick={() => {
        // Snap-aware remove when available (Minicart popover, /cart page);
        // fall back to the bare remove for any surface mounted outside
        // BasketRowsContext.
        if (removeLineItem) removeLineItem(lineItem, rowIndex);
        else remove(itemId, { line: lineItem });
      }}
      isLoading={isStale}
      className={className}
      ariaLabel="Remove item from cart"
      data-testid={`${idScope}-remove-button-${lineItem.productId}`}
    />
  );
};
BasketLineItemRemoveProduct.displayName = "BasketLineItem.RemoveProduct";

export const BasketLineItemProductQuantity = ({
  collapseAtMin = false,
}: {
  collapseAtMin?: boolean;
}) => {
  const { lineItem, idScope } = useBasketLineItem();
  const itemId = lineItem?.itemId;
  const { update, fetcher } = useBasketActions();
  const [isUpdating, setIsUpdating] = useState<false | "up" | "down">(false);

  useEffect(() => {
    if (fetcher.formData?.get("itemId") === itemId) {
      setIsUpdating(
        Number(fetcher.formData?.get("quantity")) > lineItem.quantity!
          ? "up"
          : "down",
      );
    }
  }, [fetcher, itemId, lineItem.quantity]);

  // COM-831 — the last edit this stepper submitted, so the NEXT edit can send
  // the quantity it truly starts from. The stepper re-enables when the action
  // settles, but `lineItem.quantity` only moves once the trailing basket
  // reload lands; a click in that window would otherwise report the stale
  // display value as `oldQuantity` and emit a phantom delta for a write that
  // changes nothing. Dropped when the edit failed (the line never moved).
  const lastEditRef = useRef<LastQuantityEdit | null>(null);

  // The loader's quantity is the truth once it moves (reload landed, or
  // another surface edited the line): drop the in-window bookkeeping.
  useEffect(() => {
    lastEditRef.current = null;
  }, [lineItem.quantity]);

  useOnCompleted((data, payload) => {
    if (payload?.formData?.get("itemId") !== itemId) return;
    setIsUpdating(false);
    if (isFailedEditSettle(data)) lastEditRef.current = null;
  }, fetcher);

  if (!itemId) return null;

  const current = lineItem.quantity ?? 0;
  return (
    <QuantityStepper
      quantity={current}
      onChange={n => {
        const from = resolveEditBaseline(lastEditRef.current, current);
        lastEditRef.current = nextLastEdit(current, n);
        update(itemId, n, from);
      }}
      loadingDirection={isUpdating}
      mode="standalone"
      id={`qty-${itemId}`}
      testIdPrefix={`${idScope}-quantity-${lineItem.productId}`}
      hideMinus={collapseAtMin}
    />
  );
};
BasketLineItemProductQuantity.displayName = "BasketLineItem.ProductQuantity";

export const BasketLineItemBundleItems = () => {
  const { lineItem, idScope } = useBasketLineItem();
  const { products } = useBasket() ?? {};
  const url = useURL();
  const [open, setOpen] = useState(false);

  const children = lineItem.bundledProductItems;
  if (!children?.length || !products) return null;

  const panelId = `bundle-items-${lineItem.itemId}`;

  return (
    <div className="mt-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={`${idScope}-bundle-toggle-${lineItem.productId}`}
        className="flex items-center gap-1 text-sm font-semibold text-contrast-black"
        onClick={() => setOpen(prev => !prev)}
      >
        <span>
          {open ? "Hide" : "Show"} {children.length} bundle item
          {children.length === 1 ? "" : "s"}
        </span>
        {open ? (
          <ChevronUpIcon aria-hidden className="h-4 w-4" />
        ) : (
          <ChevronDownIcon aria-hidden className="h-4 w-4" />
        )}
      </button>
      {open && (
        <Suspense fallback={null}>
          <Await resolve={products}>
            {products => {
              const productById = new Map(products.map(p => [p.id, p]));
              return (
                <ul id={panelId} className="mt-2 space-y-3">
                  {children.map(child => {
                    const childProduct = child.productId
                      ? productById.get(child.productId)
                      : undefined;
                    const image = childProduct
                      ? getVariantImage(childProduct)?.[0]
                      : undefined;
                    return (
                      <li key={child.productId} className="flex gap-3">
                        {image && (
                          <Image
                            src={image.link}
                            alt={child.productName ?? ""}
                            className="h-20 w-20 flex-shrink-0 rounded object-cover"
                          />
                        )}
                        <div className="flex min-w-0 flex-col justify-center gap-0.5 text-xs">
                          <p className="font-semibold text-contrast-black">
                            <Link
                              to={url(
                                toProductUrl({
                                  id: child.productId,
                                  name: child.productName,
                                }),
                              )}
                              prefetch="intent"
                              className="hover:underline"
                              data-testid={`${idScope}-bundle-name-${child.productId}`}
                            >
                              {child.productName}
                            </Link>
                          </p>
                          <p className="text-primary">{`SKU# ${child.productId}`}</p>
                          <p className="text-contrast-black">{`QTY: ${child.quantity}`}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              );
            }}
          </Await>
        </Suspense>
      )}
    </div>
  );
};
BasketLineItemBundleItems.displayName = "BasketLineItem.BundleItems";
